'use client';

import type { PortableTextBlock } from '@portabletext/types';
import { FORM_ID_FIELD, type FormDto, type FormFieldDto, type PrivacyPolicyLink } from '../form.types';
import { Button, Field, Fieldset, Label, Legend } from '@headlessui/react';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
    FormFieldCheckbox,
    FormFieldColor,
    FormFieldDate,
    FormFieldDateTime,
    FormFieldFile,
    FormFieldHidden,
    FormFieldInput,
    FormFieldNumber,
    FormFieldRadio,
    FormFieldRange,
    FormFieldSelect,
    FormFieldTextarea,
    FormFieldTime,
} from './formfields';
import { FormFeedback } from './FormFeedback';
import { FormSpamGuard } from './FormSpamGuard';
import { LABEL_CLASS, LABEL_OPTIONAL_CLASS } from './formfields/field.styles';
import { detectSpam } from '../spam-guard';
import { useFillDuration } from '../useFillDuration';
import { useHashPreselect } from '../useHashPreselect';
import { useRouteModalOptional } from '@/components/ui/patterns/UiRouteModal';
import { UiButtonLabel } from '@/components/ui/UiButtonLabel';
import { cn } from '@/lib/utils/tw-merge';

const SUBMIT_BUTTON_CLASS = 'btn btn-primary btn-lg disabled:pointer-events-none disabled:opacity-60';

/** Checkbox and radio fields render one tile per choice. */
function isChoiceGroup(field: FormFieldDto): boolean {
    return (field.type === 'checkbox' || field.type === 'radio') && Boolean(field.choices?.length);
}

/** The field's label, plus the marker for a field that is not required. */
function labelText(field: FormFieldDto): ReactNode {
    return (
        <>
            {field.label}
            {field.label && !field.required ? <span className={LABEL_OPTIONAL_CLASS}> (optional)</span> : null}
        </>
    );
}

/** `header` renders above the fields and hides with them while a result shows;
 *  `successText`/`errorText` replace the default result copy — all three are
 *  optional and come from the block. */
export function DynamicForm({
    form,
    privacyPolicyPage,
    header,
    successText,
    errorText,
    framed = false,
    centered = false,
}: {
    form: FormDto;
    privacyPolicyPage?: PrivacyPolicyLink;
    header?: ReactNode;
    successText?: PortableTextBlock[];
    errorText?: PortableTextBlock[];
    /** A card around the form (`relative overflow-hidden`) is the countdown bar's
     *  anchor, so the bar runs along the card's edge instead of the fields'. */
    framed?: boolean;
    /** Privacy note and submit button on the centre axis, as in a modal. */
    centered?: boolean;
}) {
    const searchParams = useSearchParams();
    // Null outside a modal → drives the dismiss behaviour and button label.
    const modal = useRouteModalOptional();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const stampFillDuration = useFillDuration();
    const formRef = useRef<HTMLFormElement>(null);
    useHashPreselect(formRef);

    // The countdown bar is position:absolute and pins to the nearest positioned
    // ancestor: the card (framed) or the modal panel, otherwise our own wrapper.
    const selfFrame = modal === null && !framed;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const formElement = e.currentTarget;
            const formData = new FormData(formElement);
            stampFillDuration(formData);

            // Honeypot filled, no JS proof or submitted too fast: show the standard
            // error and keep the values, exactly like a failed request. The route
            // checks the same signals again, since a bot can skip this code path.
            if (detectSpam(formData)) {
                setSubmitStatus('error');
                return;
            }

            const response = await fetch('/api/send-form', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                // Reset up front so the emptied form is ready behind the success panel.
                setSubmitStatus('success');
                formElement.reset();
            } else {
                // Keep the entered values on error (no reset) so the user can retry.
                setSubmitStatus('error');
            }
        } catch {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Success inside a modal closes it (animated → router.back); every other
    // case returns to the form. The form stays mounted (only hidden) while a
    // result shows, so an error keeps the entered values.
    const handleDismiss = useCallback(() => {
        if (submitStatus === 'success' && modal) {
            modal.close();
        } else {
            setSubmitStatus('idle');
        }
    }, [submitStatus, modal]);

    // Lay fields out in a 2-column grid; textareas and tile groups (and a trailing
    // odd field) span the full width so the grid never leaves a dangling half-row.
    const getFieldWidths = () => {
        if (!form.fields) return new Map<string, boolean>();

        const widths = new Map<string, boolean>();
        let currentRowSpan = 0;

        form.fields.forEach((field, index) => {
            if (!field._key) return;

            const isFullWidth = field.type === 'textarea' || isChoiceGroup(field);

            if (isFullWidth) {
                if (currentRowSpan === 1) {
                    const prevField = form.fields![index - 1];
                    if (prevField?._key) {
                        widths.set(prevField._key, true);
                    }
                }

                widths.set(field._key, true);
                currentRowSpan = 0;
            } else {
                currentRowSpan++;

                if (currentRowSpan === 2) {
                    currentRowSpan = 0;
                }

                widths.set(field._key, false);
            }
        });

        if (currentRowSpan === 1 && form.fields.length > 0) {
            const lastField = form.fields[form.fields.length - 1];
            if (lastField?._key) {
                widths.set(lastField._key, true);
            }
        }

        return widths;
    };

    const fieldWidths = getFieldWidths();

    const closesModal = submitStatus === 'success' && modal !== null;
    const dismissLabel = closesModal ? 'Modal schließen' : 'Zurück zum Formular';

    return (
        // selfFrame: our own positioned anchor for the result panel's absolute
        // countdown bar; a card or the modal panel is the anchor otherwise.
        <div className={cn(selfFrame && 'relative')}>
            {/* Kept mounted (only hidden) while a result shows, so an error keeps the entered values. */}
            <form ref={formRef} id={form.id} onSubmit={handleSubmit} hidden={submitStatus !== 'idle'}>
                <input type="hidden" name={FORM_ID_FIELD} defaultValue={form.id} />

                {header}

                <Fieldset>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {form.fields?.map((field) => {
                            const isFullWidth = fieldWidths.get(field._key!) ?? false;

                            // One control per choice: a fieldset, named by its legend.
                            if (isChoiceGroup(field)) {
                                return (
                                    <Fieldset
                                        key={field._key}
                                        className={cn('min-w-0', isFullWidth && 'md:col-span-2')}
                                    >
                                        <Legend className={LABEL_CLASS}>{labelText(field)}</Legend>
                                        {field.type === 'radio' ? (
                                            <FormFieldRadio field={field} />
                                        ) : (
                                            <FormFieldCheckbox field={field} />
                                        )}
                                    </Fieldset>
                                );
                            }

                            return (
                                <Field key={field._key} className={cn(isFullWidth && 'md:col-span-2')}>
                                    {field.type !== 'hidden' && (
                                        <Label className={LABEL_CLASS}>{labelText(field)}</Label>
                                    )}
                                    {field.type === 'textarea' && <FormFieldTextarea field={field} />}
                                    {field.type === 'select' && <FormFieldSelect field={field} />}
                                    {field.type === 'date' && <FormFieldDate field={field} />}
                                    {field.type === 'datetime-local' && <FormFieldDateTime field={field} />}
                                    {field.type === 'time' && <FormFieldTime field={field} />}
                                    {field.type === 'range' && <FormFieldRange field={field} />}
                                    {field.type === 'number' && <FormFieldNumber field={field} />}
                                    {field.type === 'file' && <FormFieldFile field={field} />}
                                    {field.type === 'color' && <FormFieldColor field={field} />}
                                    {field.type === 'hidden' && (
                                        <FormFieldHidden
                                            field={field}
                                            defaultValue={
                                                field.name ? (searchParams.get(field.name) ?? undefined) : undefined
                                            }
                                        />
                                    )}
                                    {(!field.type || ['text', 'email', 'tel', 'url'].includes(field.type)) && (
                                        <FormFieldInput field={field} />
                                    )}
                                </Field>
                            );
                        })}
                    </div>

                    {privacyPolicyPage?.slug && (
                        <p className={cn('form-note mt-6', centered && 'text-center')}>
                            <span>
                                Mit Absenden des Formulars erklären Sie sich damit einverstanden, dass Ihre Daten zur
                                Bearbeitung Ihres Anliegens verwendet werden. Weiterführende Informationen finden Sie in
                                unserer{' '}
                            </span>
                            <Link href={`/m/${privacyPolicyPage.slug}`}>Datenschutzerklärung</Link>.
                        </p>
                    )}

                    {form.submitButton && (
                        <div className={cn('mt-8 flex', centered ? 'justify-center' : 'justify-end')}>
                            <Button
                                type="submit"
                                className={cn(SUBMIT_BUTTON_CLASS, centered && 'w-full sm:w-auto')}
                                disabled={isSubmitting}
                            >
                                <UiButtonLabel>
                                    {isSubmitting ? 'Wird gesendet...' : form.submitButton.text}
                                </UiButtonLabel>
                            </Button>
                        </div>
                    )}
                </Fieldset>
                <FormSpamGuard />
            </form>

            {submitStatus === 'success' && (
                <FormFeedback
                    variant="success"
                    buttonLabel={dismissLabel}
                    onDismiss={handleDismiss}
                    autoDismiss
                    closes={closesModal}
                    content={successText}
                />
            )}

            {submitStatus === 'error' && (
                <FormFeedback
                    variant="error"
                    buttonLabel="Zurück zum Formular"
                    onDismiss={handleDismiss}
                    autoDismiss
                    content={errorText}
                />
            )}
        </div>
    );
}
